/**
 * Inventory Stock List - Logical Inventory page
 * Read-only list with FilterPanel, export, and pagination.
 * No create/edit/delete operations (data synced from upstream).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Form, Table, Col, Button, Space, message, Image } from 'antd';
import EllipsisCell from '../../../../components/common/EllipsisCell';
import { ExportOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import FilterPanel from '../../../../components/common/FilterPanel';
import ItemSelect from '../../../../components/common/ItemSelect';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import type { InventoryDTO, QueryInventoryPageRequest } from '../../../../types/inventory/stock';
import type { Paging } from '../../../../types/common';
import * as stockService from '../../../../services/inventory/stockService';

/** Format number with thousands separator */
const formatNumber = (val: number | null | undefined): string => {
  if (val === null || val === undefined) return '-';
  return val.toLocaleString();
};

/** Format timestamp to date string */
const formatDateTime = (timestamp: number | null): string => {
  return timestamp ? dayjs(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-';
};

const InventoryStockList: React.FC = () => {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dataSource, setDataSource] = useState<InventoryDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  });
  // Table body scroll height (auto-fill remaining space)
  const { tableWrapRef, scrollY } = useTableScrollHeight();

  /** Load list data */
  const loadData = useCallback(async (params: QueryInventoryPageRequest) => {
    setLoading(true);
    try {
      const response = await stockService.pageInventory(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  /** Initialize */
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  /** Build query params from form */
  const buildQueryParams = (page: number, pageSize: number): QueryInventoryPageRequest => {
    const values = searchForm.getFieldsValue();
    const params: QueryInventoryPageRequest = {
      currentPage: page,
      pageSize,
      sku: values.sku || undefined,
    };

    return params;
  };

  /** Handle filter */
  const handleFilter = () => {
    loadData(buildQueryParams(1, pagination.pageSize));
  };

  /** Handle reset */
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  /** Handle page change */
  const handlePageChange = (page: number, pageSize: number) => {
    loadData(buildQueryParams(page, pageSize));
  };

  /** Handle export - 参考订单导出方式 */
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const params: any = {
        sku: values.sku || undefined,
      };
      message.loading(t('common.message.exportingData') || 'Exporting...', 0);
      const response = await stockService.exportInventory(params);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data;
        link.download = `inventory_export_${new Date().getTime()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.destroy();
        message.success(t('inventory.stock.export.success'));
      } else {
        message.destroy();
        message.error(response.errorMessage || t('inventory.stock.export.fail'));
      }
    } catch (error: any) {
      message.destroy();
      const errorMsg = error?.response?.data?.errorMessage || t('inventory.stock.export.fail');
      message.error(errorMsg);
    } finally {
      setExportLoading(false);
    }
  };

  /** Table columns */
  const columns = [
    {
      title: t('inventory.stock.list.table.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 200,
      fixed: 'left' as const,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('inventory.stock.list.table.image'),
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (url: string | null) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: t('inventory.stock.list.table.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 150,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('inventory.stock.list.table.barcode'),
      dataIndex: 'barcode',
      key: 'barcode',
      width: 150,
      render: (val: string | null) => <EllipsisCell>{val || '-'}</EllipsisCell>,
    },
    {
      title: t('inventory.stock.list.table.categoryName'),
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 120,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('inventory.stock.list.table.availableStock'),
      dataIndex: 'availableStock',
      key: 'availableStock',
      width: 190,
      sorter: true,
      render: (val: number | null | undefined) => <EllipsisCell>{formatNumber(val)}</EllipsisCell>,
    },
    {
      title: t('inventory.stock.list.table.unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      render: (val: string) => <EllipsisCell>{val || '-'}</EllipsisCell>,
    },
    {
      title: t('inventory.stock.list.table.primaryLocation'),
      dataIndex: 'primaryLocation',
      key: 'primaryLocation',
      width: 190,
      render: (val: string | null) => <EllipsisCell>{val || '-'}</EllipsisCell>,
    },
    {
      title: t('inventory.stock.list.table.lastSyncTime'),
      dataIndex: 'lastSyncTime',
      key: 'lastSyncTime',
      width: 180,
      sorter: true,
      render: (time: number | null) => <EllipsisCell>{formatDateTime(time)}</EllipsisCell>,
    },
  ];

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Filter Panel (RULE-005) */}
      <FilterPanel
        form={searchForm}
        onFilter={handleFilter}
        onReset={handleReset}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item name="sku" label={t('inventory.stock.list.search.sku')}>
            <ItemSelect placeholder={t('inventory.stock.list.search.placeholder.sku')} />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* Action Bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '16px 0', flexShrink: 0 }}>
        <Space>
          <Button
            icon={exportLoading ? <LoadingOutlined /> : <ExportOutlined />}
            onClick={handleExport}
            loading={exportLoading}
          >
            {t('inventory.stock.list.actions.export')}
          </Button>
        </Space>
      </div>

      {/* Data Table */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          scroll={{ x: 1340, y: scrollY }}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total) => t('common.pagination.total', { total }),
            onChange: handlePageChange,
          }}
        />
      </div>
    </div>
  );
};

export default InventoryStockList;
